import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  HStack,
  Select,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import LineItemsTable from "./LineItemsTable";

const RequestForm = ({
  initialData = null,
  onSubmit,
  onCancel,
  isLoading,
  workflowOptions = [],
  showWorkflowSelect = false,
}) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    department: "",
    category: "",
    priority: "MEDIUM",
    estimatedBudget: "",
    justification: "",
    expectedDeliveryDate: "",
    workflowId: "",
  });

  const [lineItems, setLineItems] = useState([
    {
      id: Date.now(),
      itemName: "",
      description: "",
      quantity: 1,
      unitPrice: 0,
      unit: "pcs",
      totalPrice: 0,
    },
  ]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || "",
        description: initialData.description || "",
        department: initialData.department || "",
        category: initialData.category || "",
        priority: initialData.priority || "MEDIUM",
        estimatedBudget: initialData.estimatedBudget || "",
        justification: initialData.justification || "",
        expectedDeliveryDate: initialData.expectedDeliveryDate
          ? new Date(initialData.expectedDeliveryDate)
              .toISOString()
              .split("T")[0]
          : "",
        workflowId: initialData.workflowId?._id || initialData.workflowId || "",
      });
      if (initialData.lineItems && initialData.lineItems.length > 0) {
        setLineItems(initialData.lineItems);
      }
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Title validation (min 5 characters)
    if (!formData.title || formData.title.trim().length < 5) {
      newErrors.title = "Title must be at least 5 characters";
    }

    // Description validation (min 10 characters)
    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    // Department required
    if (!formData.department) {
      newErrors.department = "Department is required";
    }

    // Category required
    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    // Line items validation
    if (lineItems.length === 0) {
      newErrors.lineItems = "At least one line item is required";
      toast({
        title: "Validation Error",
        description: "Please add at least one line item",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } else {
      // Validate each line item
      const invalidItems = lineItems.filter(
        (item) =>
          !item.itemName ||
          item.itemName.trim() === "" ||
          !item.quantity ||
          item.quantity <= 0 ||
          !item.unitPrice ||
          item.unitPrice < 0,
      );
      if (invalidItems.length > 0) {
        newErrors.lineItems =
          "All line items must have name, quantity > 0, and unit price >= 0";
        toast({
          title: "Validation Error",
          description: "Please complete all line item fields",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const requestData = {
        ...formData,
        lineItems: lineItems.map(({ id, ...item }) => item), // Remove temporary IDs
      };

      if (!requestData.workflowId) {
        delete requestData.workflowId;
      }

      onSubmit(requestData);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        {/* Title */}
        <FormControl isInvalid={errors.title} isRequired>
          <FormLabel>Request Title</FormLabel>
          <Input
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            placeholder="e.g., Office Supplies for Q1 2026"
          />
          <FormErrorMessage>{errors.title}</FormErrorMessage>
        </FormControl>

        {/* Description */}
        <FormControl isInvalid={errors.description} isRequired>
          <FormLabel>Description</FormLabel>
          <Textarea
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Detailed description of the procurement request"
            rows={4}
          />
          <FormErrorMessage>{errors.description}</FormErrorMessage>
        </FormControl>

        {/* Department & Category */}
        <HStack spacing={4}>
          <FormControl isInvalid={errors.department} isRequired flex={1}>
            <FormLabel>Department</FormLabel>
            <Select
              value={formData.department}
              onChange={(e) => handleInputChange("department", e.target.value)}
              placeholder="Select department"
            >
              <option value="IT">IT</option>
              <option value="HR">HR</option>
              <option value="Finance">Finance</option>
              <option value="Operations">Operations</option>
              <option value="Marketing">Marketing</option>
              <option value="Sales">Sales</option>
              <option value="Admin">Admin</option>
            </Select>
            <FormErrorMessage>{errors.department}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={errors.category} isRequired flex={1}>
            <FormLabel>Category</FormLabel>
            <Select
              value={formData.category}
              onChange={(e) => handleInputChange("category", e.target.value)}
              placeholder="Select category"
            >
              <option value="Office Supplies">Office Supplies</option>
              <option value="IT Equipment">IT Equipment</option>
              <option value="Furniture">Furniture</option>
              <option value="Software">Software</option>
              <option value="Services">Services</option>
              <option value="Travel">Travel</option>
              <option value="Marketing Materials">Marketing Materials</option>
              <option value="Other">Other</option>
            </Select>
            <FormErrorMessage>{errors.category}</FormErrorMessage>
          </FormControl>
        </HStack>

        {/* Priority & Estimated Budget */}
        <HStack spacing={4}>
          <FormControl flex={1}>
            <FormLabel>Priority</FormLabel>
            <Select
              value={formData.priority}
              onChange={(e) => handleInputChange("priority", e.target.value)}
            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent</option>
            </Select>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel>Estimated Budget (₹)</FormLabel>
            <Input
              type="number"
              value={formData.estimatedBudget}
              onChange={(e) =>
                handleInputChange("estimatedBudget", e.target.value)
              }
              placeholder="Enter estimated budget"
            />
          </FormControl>
        </HStack>

        {/* Expected Delivery Date */}
        <FormControl>
          <FormLabel>Expected Delivery Date</FormLabel>
          <Input
            type="date"
            value={formData.expectedDeliveryDate}
            onChange={(e) =>
              handleInputChange("expectedDeliveryDate", e.target.value)
            }
          />
        </FormControl>

        {showWorkflowSelect && (
          <FormControl>
            <FormLabel>Workflow</FormLabel>
            <Select
              value={formData.workflowId}
              onChange={(e) => handleInputChange("workflowId", e.target.value)}
              placeholder="Select workflow"
            >
              {workflowOptions.map((workflow) => (
                <option
                  key={workflow._id}
                  value={workflow._id}
                  disabled={!workflow.isActive}
                >
                  {workflow.name}
                  {!workflow.isActive ? " (Inactive)" : ""}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Justification */}
        <FormControl>
          <FormLabel>Justification</FormLabel>
          <Textarea
            value={formData.justification}
            onChange={(e) => handleInputChange("justification", e.target.value)}
            placeholder="Business justification for this procurement"
            rows={3}
          />
        </FormControl>

        {/* Line Items */}
        <FormControl isInvalid={errors.lineItems} isRequired>
          <FormLabel>Line Items</FormLabel>
          <LineItemsTable lineItems={lineItems} setLineItems={setLineItems} />
          <FormErrorMessage>{errors.lineItems}</FormErrorMessage>
        </FormControl>

        {/* Action Buttons */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={onCancel} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" colorScheme="blue" isLoading={isLoading}>
            {initialData ? "Update Request" : "Create Request"}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default RequestForm;
